import React from "react";
import {
  useAuthentication,
  useRetrieveCurrentPlanzone,
} from "@planzone/view-models";
import { useFilesCache } from "@components/cache";
import { Props } from "./types";
import { Context, useLogoLoader } from "./context";

function LogoLoader({ children, planzone: providedPlanzone }: Props) {
  const { planzone: p } = useRetrieveCurrentPlanzone();
  const planzone = providedPlanzone || p;
  const cache = useFilesCache();
  const { token } = useAuthentication().account;
  const [state, setState] = React.useState({
    loadingState: "loadingCache",
    uri: "",
  });
  const prevLogo = React.useRef(planzone.data.logo);

  React.useEffect(() => {
    if (prevLogo.current !== planzone.data.logo) {
      prevLogo.current = planzone.data.logo;
      setState({ loadingState: "loadingCache", uri: "" });
    }
  }, [planzone.data.logo]);

  React.useEffect(() => {
    state.loadingState === "loadingCache" &&
      !!planzone.data.logo &&
      cache
        .get(planzone.data.logo)
        .then((blob) =>
          setState({
            loadingState: "startLoading",
            uri: URL.createObjectURL(blob),
          }),
        )
        .catch(() => setState({ loadingState: "none", uri: "" }));
  }, [planzone.data.logo, state.loadingState === "loadingCache"]);

  React.useEffect(() => {
    if (state.loadingState !== "none" || !planzone.data.logo) return;

    const controller = new AbortController();
    const signal = controller.signal;

    fetch(`/docs/files/documents/${planzone.data.logo}/content`, {
      method: "get",
      priority: "low",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      signal,
    })
      .then((res) => res.blob())
      .then((blob) => {
        const uri = URL.createObjectURL(blob);

        URL.revokeObjectURL(state.uri);

        try {
          cache.add(planzone.data.logo, blob);
        } catch (e) {}

        setState({ loadingState: "startLoading", uri });
      })
      .catch(() => {
        fetch(planzone.data.logo, {
          method: "get",
          priority: "low",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal,
        })
          .then((res) => res.blob())
          .then((blob) => {
            const uri = URL.createObjectURL(blob);

            URL.revokeObjectURL(state.uri);

            try {
              cache.add(planzone.data.logo, blob);
            } catch (e) {}

            setState({ loadingState: "startLoading", uri });
          })
          .catch(() => {
            setState((s) => ({
              ...s,
              loadingState: "failure",
            }));
          });
      });

    return () => {
      controller.abort();
    };
  }, [planzone.data.logo, state.loadingState === "none"]);

  React.useEffect(() => {
    if (state.loadingState === "startLoading") {
      const img = new window.Image();

      img.src = state.uri;

      img.onload = () => {
        setState({ ...state, loadingState: "success" });
        img.remove();
      };
      img.onerror = () => {
        setState({ ...state, loadingState: "failure" });
        img.remove();
      };
    }
  }, [state.loadingState, state.uri]);

  return <Context.Provider value={state.uri}>{children}</Context.Provider>;
}

export { LogoLoader, useLogoLoader };
